import { Observable, mergeMap, map, iif, defer, throwError, take, shareReplay, firstValueFrom } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../../environments/environment';
import { Category, Establishment, EstablishmentFull, FeedCategory, LikeType, MyListItem, UnauthenticatedError } from '../../models';
import { AuthService } from '../auth';
import { EstablishmentMap } from 'src/app/models/establishment-map.model';
import { Config } from 'src/app/models/config.model';
import { Slot } from 'src/app/models/slot.model copy';
import { NewReservation } from 'src/app/models/new-reservation';
import { Booking } from 'src/app/models/booking';

const HOME_FEED_URL = '/home';
const LIKE_URL = '/likes/like';
const DISLIKE_URL = '/likes/dislike';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  headers$ = this.authService.accessToken$.pipe(
    map((at: string) => ({
      ...(!!at && {
        Authorization: `Bearer ${at}`,
      }),
    })),
  );

  headersAuthorized$ = this.authService.accessToken$.pipe(
    mergeMap((at: string) => iif(
      () => !!at,
      defer(() => this.headers$),
      defer(() => throwError(() => new UnauthenticatedError('Unauthenticated'))),
    )),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
  ) { }


  getConfig(): Observable<Config> {
    const url = `${environment.apiHostUrl}/config`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<Config>(url, { headers })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getDemoData(): Observable<Establishment[]> {
    const url = `${environment.apiHostUrl}/demo/establishments`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, { headers })),
    ).pipe(
      map(({ places }) => places),
    );;
  }

  getDemoProposals(ids: String[]): Observable<Establishment[]> {
    const url = `${environment.apiHostUrl}/demo/likes`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.post<any>(url, {
        likedSrcIds: ids
      }, { headers })),
    ).pipe(
      map(({ places }) => places),
    );;
  }

  getHomeFeed(lat: number = 0, lng: number = 0): Observable<FeedCategory[]> {
    let params = {};

    if (lat > 0) {
      params = { lat: lat, lng: lng }
    }

    const url = `${environment.apiHostUrl}${HOME_FEED_URL}`;
    return this.headers$.pipe(
      take(1),
      mergeMap((headers) => this.http.get<any>(url, {
        params,
        headers,
      })),
      map(({ categories }) => categories),
    );
  }

  getHomeFeedCategory(id: string, offset: number = 0): Observable<Establishment[]> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}${HOME_FEED_URL}/category/${id}?offset=${offset}&limit=20`;
    return this.http.get<any>(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).pipe(
      map(({ places }) => places),
    );
  }

  getMapFeed(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
    term: string = '',
    typeId: string = '',
    cuisineId: string = '',
    occasionId: string = '',
    delivery: boolean = false,
    pickup: boolean = false,
    breakfast: boolean = false,
    lunch: boolean = false
  ): Observable<EstablishmentMap[]> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    let params: any = {
      lat1: lat1,
      lng1: lng1,
      lat2: lat2,
      lng2: lng2
    }

    if (typeId != '') params.typeId = typeId;
    if (cuisineId != '') params.cuisineId = cuisineId;
    if (occasionId != '') params.occasionId = occasionId;
    if (term != '') params.term = term;

    params.delivery = delivery;
    params.pickup = pickup;
    params.breakfast = breakfast;
    params.lunch = lunch;

    const url = `${environment.apiHostUrl}/map`;
    return this.http.get<any>(url, {
      params: params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).pipe(
      map(({ establishments }) => establishments),
    );
  }

  getEstablishment(id: string): Observable<EstablishmentFull> {
    const url = `${environment.apiHostUrl}/establishments/${id}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<EstablishmentFull>(url, { headers })),
    );
  }

  getMenu(id: string): Observable<any> {
    const url = `${environment.apiHostUrl}/establishment/${id}/menu`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, { headers })),
    );
  }

  async like(estId: string, type: LikeType) {
    const path = type === LikeType.Like ? LIKE_URL : DISLIKE_URL;

    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    console.log(token)
    this.http.put(`${environment.apiHostUrl}${path}/${estId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async deleteLike(estId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    this.http.delete(`${environment.apiHostUrl}/likes/${estId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async likeItem(itemId: string, type: LikeType) {
    const path = type === LikeType.Like ? LIKE_URL : DISLIKE_URL;
    let token = '';

    this.authService.accessToken$.subscribe(res => { token = res; });

    console.log(token)
    this.http.put(`${environment.apiHostUrl}${path}/menu-item/${itemId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async deleteLikeItem(itemId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    this.http.delete(`${environment.apiHostUrl}/likes/menu-item/${itemId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async saveItem(itemId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    this.http.put(`${environment.apiHostUrl}/saves/menu-item/${itemId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async deleteSaveItem(itemId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    await this.http.delete(`${environment.apiHostUrl}/saves/menu-item/${itemId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async save(estId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    this.http.put(`${environment.apiHostUrl}/saves/${estId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async deleteSave(estId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    await this.http.delete(`${environment.apiHostUrl}/saves/${estId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async updateComment(estId: string, comment: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    return this.http.post(`${environment.apiHostUrl}/saves/${estId}/comment`, {
      message: comment
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async updateCommentLike(likeId: string, comment: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    return this.http.post(`${environment.apiHostUrl}/likes/${likeId}/comment`, {
      message: comment
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  getSlots(estId: string, date: string, guests: number): Observable<Slot[]> {
    const url = `${environment.apiHostUrl}/${estId}/reservations/slots?date=${date}&guests=${guests}`;
    return this.http.get<{ slots: Slot[] }>(url).pipe(
      map(({ slots }) => slots),
    );
  }

  async createReservation(estId: string, newReservation: NewReservation) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    return this.http.post(`${environment.apiHostUrl}/${estId}/reservations`, newReservation, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  getGuideCategoryPost(id: string, lat: number = 0, lng: number = 0): Observable<Pick<Category, 'places'>> {
    const url = `${environment.apiHostUrl}/guide/category/${id}`;
    let params: any = {};

    if (lat > 0) {
      params = { lat, lng };
    }

    return this.headers$.pipe(
      mergeMap((headers) => this.http.post<{ establishments: Establishment[] }>(url, {}, {
        params,
        headers
      })),
      map(({ establishments }) => ({ places: establishments })),
    );
  }

  getGuideCategory(id: string): Observable<any> {
    const url = `${environment.apiHostUrl}/guide/category/${id}`;
    let params: any = {};

    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<{ places: Establishment[] }>(url, {
        headers
      })),
      map(({ places }) => ({ places })),
    );
  }

  getQuickChoice(genre: string, lat: number = 0, lng: number = 0): Observable<Pick<Category, 'places'>> {
    const url = `${environment.apiHostUrl}/establishments/quick-choices/${genre}`;
    let params: any = {};

    if (lat > 0) {
      params = { lat, lng };
    }

    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<{ establishments: Establishment[] }>(url, {
        params,
        headers
      })),
      map(({ establishments }) => ({ places: establishments })),
    );
  }

  getCategory(id: string): Observable<Category> {
    const url = `${environment.apiHostUrl}/category/${id}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<Category>(url, { headers })),
    );
  }

  search(
    term: string,
    typeId: string = '',
    cuisineId: string = '',
    occasionId: string = '',
    neighborhoodId: string = '',
    delivery: boolean = false,
    pickup: boolean = false,
    breakfast: boolean = false,
    lunch: boolean = false
  ): Observable<Establishment[]> {

    let params: any = {};
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    if (term != '') params.term = term;
    if (typeId != '') params.typeId = typeId;
    if (cuisineId != '') params.cuisineId = cuisineId;
    if (occasionId != '') params.occasionId = occasionId;
    if (neighborhoodId != '') params.neighborhoodId = neighborhoodId;

    params.delivery = delivery;
    params.pickup = pickup;
    params.breakfast = breakfast;
    params.lunch = lunch;

    return this.http.get<{ establishments: Establishment[] }>(`${environment.apiHostUrl}/search`, {
      params: params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).pipe(
      map(({ establishments }) => establishments),
    );
  }

  async getMyLists(): Promise<Observable<MyListItem[]>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });


    const url = `${environment.apiHostUrl}/my-lists`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<MyListItem[]>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  getMyMenu(): Observable<MyListItem[]> {
    const url = `${environment.apiHostUrl}/my-lists/menu-views`;
    return this.headersAuthorized$.pipe(
      mergeMap((headers) => this.http.get<MyListItem[]>(url, { headers })),
    );
  }

  async getBookings(): Promise<Observable<Booking[]>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });


    const url = `${environment.apiHostUrl}/reservations`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<Booking[]>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async getNotifications_old(): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });


    const url = `${environment.apiHostUrl}/notifications`;

    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any[]>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  getNotifications(): Observable<any> {
    const url = `${environment.apiHostUrl}/notifications`;
    return this.headersAuthorized$.pipe(
      mergeMap((headers) => this.http.get<any>(url, { headers })),
    );
  }

  async readNotification(timestamp: any) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });
    const url = `${environment.apiHostUrl}/notifications/mark-as-read?timestamp=${timestamp}`;

    await this.http.patch<any>(url, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(result => {
      return result;
    });
  }


  async getVacancies(id: string): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/${id}/job-openings`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async getApplications(): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/job-applications`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async getApplication(id: string): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/job-applications/${id}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async readApplication(id: string): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/job-applications/${id}/read`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.put<any>(url, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async sendVacancy(estId: string, vacancyId: string, data: object): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    let url = `${environment.apiHostUrl}/${estId}/job-openings/${vacancyId}/application`;

    return this.headers$.pipe(
      mergeMap((headers) => this.http.post<any>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));

    // return this.http.post(`${environment.apiHostUrl}/${estId}/job-openings/${vacancyId}/application`, data, {
    //   headers: {
    //     Authorization: `Bearer ${token}`
    //   }
    // }).subscribe(res => { })
  }

  async sendRequest(data: object): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    let url = `${environment.apiHostUrl}/for-restaurants/onboard-new`;

    return this.headers$.pipe(
      mergeMap((headers) => this.http.post<any>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));

    // return this.http.post(`${environment.apiHostUrl}/${estId}/job-openings/${vacancyId}/application`, data, {
    //   headers: {
    //     Authorization: `Bearer ${token}`
    //   }
    // }).subscribe(res => { })
  }

  async sendRequestExisting(data: object, establishmentId: string): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    let url = `${environment.apiHostUrl}/for-restaurants/manage-existing/${establishmentId}`;

    return this.headers$.pipe(
      mergeMap((headers) => this.http.post<any>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));

    // return this.http.post(`${environment.apiHostUrl}/${estId}/job-openings/${vacancyId}/application`, data, {
    //   headers: {
    //     Authorization: `Bearer ${token}`
    //   }
    // }).subscribe(res => { })
  }

  async getServices() {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/customer-service/tickets`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async addService(subject: string, message: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/customer-service/tickets`;

    return this.http.post(url, {
      subject: subject,
      message: message
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })


  }

  async getTicket(ticket: any) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/customer-service/tickets/${ticket.id}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async sendMessage(message: string, ticketId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/customer-service/tickets/${ticketId}/message`;

    return this.http.post(url, {
      message: message
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async sendMessageApplication(message: string, applicationId: string) {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/job-applications/${applicationId}/message`;
    return this.http.post(url, {
      message: message
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).subscribe(res => { })
  }

  async submitGuide(categoryId: string): Promise<any> {
    const token = await firstValueFrom(this.authService.accessToken$);
  
    const url = `${environment.apiHostUrl}/guide/submit/${categoryId}`;
  
    return firstValueFrom(
      this.http.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
    );
  }


  async getStoredUserProfile(): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/job-applications/stored-user-profile`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  getSocialProfile(): Observable<any> {
    const url = `${environment.apiHostUrl}/social/profile`;
    return this.headersAuthorized$.pipe(
      mergeMap((headers) => this.http.get<any>(url, { headers })),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      }),
    );
  }

  async getFollowings(): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/social/followings`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async deleteFollowing(followingId): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/social/follow/${followingId}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.delete<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async getRecommendations(foolowingIds, categoryId, lat, lng): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    console.log({ params: { foolowingIds, categoryId } })

    const url = `${environment.apiHostUrl}/social/recommendations`;

    console.log({ lat, lng })

    let params = {};
    if (lat > 0 && lng > 0) {
      params = { lat, lng };
    }

    return this.headers$.pipe(
      mergeMap((headers) => this.http.post<any>(url, { foolowingIds, categoryId }, {
        params: params,
        headers: {
          Authorization: `Bearer ${token}`
        },

      })));
  }

  async getRecommendationsById(id): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/social/recommendations/${id}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        },

      })));
  }

  async follow(refCode): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/social/follow/${refCode}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.post<any>(url, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }

  async socialSearch(term): Promise<Observable<any>> {
    let token = '';
    this.authService.accessToken$.subscribe(res => { token = res; });

    const url = `${environment.apiHostUrl}/social/search?term=${term}`;
    return this.headers$.pipe(
      mergeMap((headers) => this.http.get<any>(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })));
  }
}
